import React, {useEffect, useState} from 'react';
import {Button, Form, Row, Select, Space} from 'antd';
import "../../assets/css/Dentobook.css";
import "../../assets/css/PatientPhotos.css";
import {
    Appointment,
    DentalActivityType,
    Patient,
    StatusResponse,
    Tooth,
    ToothConditionCode,
    ToothWithRelationships
} from "../../api-client";
import {FormattedMessage, useIntl} from "react-intl";
import {CaretDownOutlined, EditOutlined, PlusOutlined} from "@ant-design/icons";
import PatientAppointmentModal from "./PatientAppontmentModal";
import PatientToothStatusCard from "./PatientToothStatusCard";
import PatientTreatmentPlanCard from "./PatientTreatmentPlanCard";
import {getPatientApi, getMedicalStaffApi, getNZIZApi, getStatusesApi} from "../../api/apiInstances";
import {useForm} from "antd/es/form/Form";
import "../../assets/css/PatientTreatment.css";
import {isMobileOnly} from "react-device-detect";
import logger from "../../utils/logger";

interface PatientTreatmentProps {
    patient: Patient;
    selectedAppointment: Appointment;
    setSelectedAppointment: Function;
    tab: string;
    setTab: Function;
}

const PatientTreatment = ({patient, selectedAppointment, setSelectedAppointment, tab, setTab}: PatientTreatmentProps) => {

    const [multiSelectionMode, setMultiSelectionMode] = useState<boolean>(false);
    const [mode, setMode] = useState<'create' | 'edit' | ''>('');
    const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState<boolean>(false);
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [appointmentsMap, setAppointmentsMap] = useState<Map<string, Appointment>>(new Map());
    const [toothTab, setToothTab] = useState(0);

    const [teethPositions, setTeethPositions] = useState<ToothWithRelationships[]>([]);
    const [statuses, setStatuses] = useState<StatusResponse[]>([]);
    const [nzisToothConditionCodes, setNzisToothConditionCodes] = useState<ToothConditionCode[]>([]);
    const [nzisActivities, setNzisActivities] = useState<DentalActivityType[]>([]);

    const appointmentForm = useForm()[0];

    const patientApi = getPatientApi();
    const medicalApi = getMedicalStaffApi();
    const nzisApi = getNZIZApi();
    const statusesApi = getStatusesApi();

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.ctrlKey || e.key === 'Meta') {
            setMultiSelectionMode(true);
        }
    };

    const handleKeyUp = () => {
        setMultiSelectionMode(false);
    };

    useEffect(() => {
        if (tab === 'treatment') {
            setToothTab(1);
            setTab('');
        }

        window.addEventListener('keydown',  handleKeyDown, true);
        window.addEventListener('keyup', handleKeyUp, true);

        loadData();

        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
            window.removeEventListener('keyup', handleKeyUp, true);
        };
    }, []);

    const loadData = () => {
        if (!patient.id) {
            return;
        }
        patientApi.fetchPatientAppointments(patient.id)
            .then(res => {
                const appointmentsArray = res.data
                    .sort((appointmentOne, appointmentTwo) =>
                        appointmentTwo.createdAt?.localeCompare(appointmentOne.createdAt!)!);

                setAppointments(appointmentsArray);
                if (res.data.length) {
                    res.data = res.data
                        .sort((appointmentItemOne, appointmentItemTwo) =>
                            appointmentItemTwo.createdAt?.localeCompare(appointmentItemOne.createdAt!)!);
                    const map = new Map();
                    for (const appointmentItem of res.data) {
                        map.set(appointmentItem.id, appointmentItem);
                    }

                    setAppointmentsMap(map);

                    if (mode === 'create' || !selectedAppointment?.id) {
                        setSelectedAppointment(res.data[0]);
                        appointmentForm.setFieldValue('appointmentName', res.data[0].id);
                    }
                    setMode('');
                }

                statusesApi.fetchAllStatuses()
                    .then(res => {
                        setStatuses(res.data);
                        nzisApi.fetchToothConditionCodes()
                            .then(res => {
                                setNzisToothConditionCodes(res.data);
                                nzisApi.fetchDentalActivityTypes()
                                    .then(res => {
                                        setNzisActivities(res.data);
                                    })
                                    .catch(err => logger.log(err))
                            })
                            .catch(err => logger.log(err))
                    });
            })
            .catch(err => logger.log(err));
    };

    const fetchTeeth = () => {
        if (!selectedAppointment) {
            return;
        }

        return patientApi.fetchPatientTeeth(selectedAppointment.id!)
            .then(res => {
                setTeethPositions(res.data.sort((toothOne, toothTwo) => {
                    if (!toothOne.orderNumber || !toothTwo.orderNumber) return 0;

                    return toothOne.orderNumber - toothTwo.orderNumber;
                }));
            })
            .catch(err => logger.log(err));
    };

    const openAppointmentModal = (modalMode: 'create' | 'edit') => {
        setMode(modalMode);
        setIsAppointmentModalOpen(true);
    };

    return (
        <>
            <PatientAppointmentModal mode={mode}
                                     appointment={selectedAppointment}
                                     patient={patient} loadData={loadData}
                                     appointments={appointments}
                                     isModalOpen={isAppointmentModalOpen} setIsModalOpen={setIsAppointmentModalOpen}/>
            <div className="dentobook-content">
                <Row className="mt-16 mb-12">
                    <Form layout="vertical" form={appointmentForm}>
                        <Form.Item name="appointmentName"
                                   label={<FormattedMessage id="appointments"/>}
                                   className={"dentobook-form-item"}
                                   initialValue={selectedAppointment?.id}
                        >
                            <Select
                                style={!isMobileOnly ? {width: '70vw'} : {}}
                                value={selectedAppointment?.id}
                                size={"large"}
                                className="icon-select"
                                suffixIcon={<CaretDownOutlined/>}
                                placeholder={useIntl().formatMessage({id: 'choose_appointment'})}
                                onChange={e => setSelectedAppointment(appointmentsMap.get(e)!)}
                            >
                                {appointments.map(appointment => (
                                    <Select.Option key={appointment.id} value={appointment.id}>
                                        {appointment.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Form>
                    <Space direction="horizontal" className="mt-8">
                        <Button disabled={!selectedAppointment?.id} shape="circle" size="large" className="ml-16"
                                onClick={() => openAppointmentModal('edit')}><EditOutlined
                            className="color-primary"/></Button>
                        <Button shape="circle" size="large" className="ml-4"
                                onClick={() => openAppointmentModal('create')}><PlusOutlined
                            className="color-primary"/></Button>
                    </Space>

                </Row>
                {selectedAppointment?.id ?
                    <>
                        <Button className="tooth-status-btn"
                                type={toothTab === 0 ? "primary" : "default"}
                                onClick={() => setToothTab(0)}><FormattedMessage
                            id="tooth_status"/></Button>
                        <Button className="tooth-plan-btn"
                                type={toothTab === 1 ? "primary" : "default"}
                                onClick={() => setToothTab(1)}><FormattedMessage
                            id="treatment_plan"/></Button>
                        {toothTab === 0 ?
                            <PatientToothStatusCard multiSelectionMode={multiSelectionMode}
                                                    appointment={selectedAppointment}
                                                    fetchTeeth={fetchTeeth}
                                                    teethPositions={teethPositions}
                                                    statuses={statuses}
                                                    nzisToothConditionCodes={nzisToothConditionCodes}
                                                    patient={patient}/> :
                            <PatientTreatmentPlanCard multiSelectionMode={multiSelectionMode}
                                                      appointment={selectedAppointment}
                                                      fetchTeeth={fetchTeeth}
                                                      statuses={statuses}
                                                      teethPositions={teethPositions}
                                                      nzisActivities={nzisActivities}
                                                      nzisToothConditionCodes={nzisToothConditionCodes}
                                                      patient={patient}/>}
                    </>
                    : <h3><FormattedMessage id="no_selected_appointment"/></h3>}
            </div>
        </>
    );
};

export default PatientTreatment;